<template>
  <div class="accordion-container tw-w-full tw-bg-inherit">
    <div
      :class="`tw-sticky tw-top-0 tw-z-10 tw-bg-white tw-py-1 dark:tw-bg-black ${
        disabled ? 'tw-cursor-not-allowed' : ''
      } ${extraClasses ? extraClasses : ''}`"
    >
      <button
        data-testid="accordion-toggle-button"
        class="tw-flex tw-w-full tw-items-center tw-text-left"
        :disabled="disabled"
        @click.stop="handleToggleAccordion"
      >
        <div v-if="chevronPosition === 'left'">
          <TablerChevronDown v-if="open" class="tw-h-5 tw-w-5" />
          <TablerChevronRight v-else class="tw-h-5 tw-w-5" />
        </div>
        <div id="accordion-slot-head" class="tw-pl-2">
          <slot name="head"></slot>
        </div>
        <div v-if="chevronPosition === 'right'">
          <TablerChevronDown v-if="open" class="tw-h-5 tw-w-5" />
          <TablerChevronRight v-else class="tw-h-5 tw-w-5 -tw-rotate-90" />
        </div>
        <div v-if="draggable" class="drag-handle tw-ml-auto tw-cursor-grab active:tw-cursor-grabbing" @click.stop>
          <TablerGripVertical class="tw-h-5 tw-w-5 tw-text-slate-300" />
        </div>
      </button>
    </div>
    <Transition
      name="accordion-details"
      @before-enter="beforeEnter"
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-enter="onAfterEnter"
    >
      <div v-if="open" class="accordion-details-body tw-pt-4" :class="detailsExtraClasses">
        <slot name="details"></slot>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import TablerChevronDown from './Icons/tabler/TablerChevronDown.vue';
import TablerChevronRight from './Icons/tabler/TablerChevronRight.vue';
import TablerGripVertical from './Icons/tabler/TablerGripVertical.vue';

export default defineComponent({
  name: 'StudioAccordion',
  components: {
    TablerChevronDown,
    TablerChevronRight,
    TablerGripVertical
  },
  props: {
    draggable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    extraClasses: {
      type: String,
      default: ''
    },
    isExpanded: {
      type: Boolean,
      default: true
    },
    chevronPosition: {
      type: String,
      default: 'left'
    },
    detailsExtraClasses: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: true
    };
  },
  beforeMount() {
    this.open = this.isExpanded;
  },
  methods: {
    beforeEnter: function (el: Element) {
      (el as HTMLElement).style.height = '0';
    },
    enter: function (el: Element) {
      (el as HTMLElement).style.height = el.scrollHeight + 'px';
    },
    beforeLeave: function (el: Element) {
      (el as HTMLElement).style.height = el.scrollHeight + 'px';
    },
    leave: function (el: Element) {
      const height = getComputedStyle(el).height;

      (el as HTMLElement).style.height = height;

      // Force repaint to make sure the
      // animation is triggered correctly.
      getComputedStyle(el).height;

      requestAnimationFrame(() => {
        (el as HTMLElement).style.height = '0';
      });
    },
    onAfterEnter: function (el: Element) {
      (el as HTMLElement).style.height = 'auto';
    },
    handleToggleAccordion() {
      this.open = !this.open;
      this.$emit('onToggleAccordion', this.open);
    },
    /** @public */
    handleCloseAccordion() {
      this.open = false;
    },
    /** @public */
    handleOpenAccordion() {
      this.open = true;
    }
  }
});
</script>
